<script lang="ts" setup>
import { useLocaleStoreRef, useUserStoreRef } from '@/stores';
import { reactive, ref, onMounted, type ComponentInstance } from 'vue';
import { UserDao } from '@/dao';
import {
    ContentComp,
    ButtonComp,
    LanguageSelectorComp,
    IconComp,
    SplashComp,
    ModalComp,
    InputCode,
    InputCheckBox,
    AlertMessage,
} from '@/components';
import { useHelper, useTaskLoader, useGoogleAuthentication } from '@/composeables';
import { type ManagerUser, LoginRequest, type JsonWebToken, ResetPasswordRequest } from '@/objects/generated';
import { featureIcons } from '../Menu/featureicons';
import ResetPasswordView from './Login/ResetPasswordView.vue';
import router from '@/router';
import cookie from 'cookiejs';

const { user } = useUserStoreRef();
const { AddLoader, LoadState } = useTaskLoader();

const { locale } = useLocaleStoreRef();

const resetPasswordView = ref<ComponentInstance<typeof ResetPasswordView>>();

// Refs to the modals
const lostpassswordModal = ref<ComponentInstance<typeof ModalComp>>();
const newaccountModal = ref<ComponentInstance<typeof ModalComp>>();
const twofactorModal = ref<ComponentInstance<typeof ModalComp>>();

const invalidTwofactorCode = ref<boolean>(false);

/**
 * Set up the user request model for username and password login
 */
const userReq = reactive<LoginRequest>(
    LoginRequest.FromJsValues({ Username: '', Password: '', TrustDevice: false, TwoFactorCode: '', Language: locale.value.Locale }),
);

/**
 * Set up the reset password request model
 */
const resetPasswordRequest = ref<ResetPasswordRequest>(
    ResetPasswordRequest.FromJsValues({ Password: '', Code: '', Username: '', Language: locale.value.Locale, ConfirmPassword: '' }),
);

/**
 * We create a device fingerprint for the user. This is used to identify the user device.
 * This should be as unique as possible to avoid conflicts with other devices. But it doesnt need to be 100% unique since the username and password is also checked.
 */
const createDeviceFingerprint = () => {
    // Check if we have a cookie called EQDeviceFingerprint. If not we create a new one

    let fingerprint: string | boolean = cookie.get('EQDeviceFingerprint');
    if (fingerprint === false) {
        // We create a new fingerprint
        fingerprint = '';
        try {
            fingerprint = self.crypto.randomUUID();
        } catch (error) {
            // If we cannot create a fingerprint, this device cannot be trusted
            return '';
        }

        // We create a date 1 year in the future for expiration
        let expiration = new Date(new Date().getTime() + 31536000 * 1000);

        cookie.set('EQDeviceFingerprint', fingerprint, {
            path: '/',
            domain: '.eqtiming.com',
            expires: expiration,
            sameSite: 'Lax',
            secure: true,
        });

        return fingerprint;
    }
    return fingerprint?.toString();
};

// We assign the device fingerprint to the user request
userReq.DeviceFingerprint = createDeviceFingerprint();

/**
 * Ref to the Error message
 */
const errormessage = ref<string>('');

/**
 * Ref to the success message
 */
const successmessage = ref<string>('');

/**
 * Handle the login. This is just a helper function to avoid code duplication
 */
const login = async (loginfn: () => Promise<ManagerUser | null>) => {
    AddLoader(async () => {
        try {
            let cookieExists = document.cookie.match(/^(.*;)?["EQSession"|"EQSessionDev"]*=\s*[^;]+(.*)?$/);
            user.value = await loginfn();

            // We reset the user request
            userReq.Password = '';
            userReq.TwoFactorCode = '';
            userReq.TrustDevice = false;
            invalidTwofactorCode.value = false;

            // There is an issue with the cookie handling if the user is not logged in. This is a workaround to fix that.
            // The issue is that if there is not allready set a cookie, and we set one via the async login, then the vue app cannot read the cookie back until the page is reloaded.
            // if the cookie allready exists, this is not an issue.
            if (!cookieExists) {
                router.go(0);
            }
        } catch (error: unknown) {
            // We need to handle a twofactor request here
            if ((error as Error).cause == '403') {
                if ((userReq.TwoFactorCode ?? '') == '') {
                    twofactorModal.value?.show();
                } else {
                    invalidTwofactorCode.value = true;
                }
            } else {
                errormessage.value = (error as Error).message;
            }
        } finally {
            useHelper().setFocus();
        }
    }, 'login.user');
};

/**
 * Handle the login request
 */
const loginUserRequest = async () => {
    login(() => UserDao.Login(userReq));
};

/**
 * Handle the login with oauth2
 * @param payload
 */
const LoginOauth2 = async (payload: JsonWebToken) => {
    login(() => UserDao.LoginOauth2(payload));
};

/**
 * Handle the password updated event
 * @param status - true if the password was updated, should never be false since that is an error in backend
 */
const passwordUpdated = (status: boolean) => {
    if (status) {
        successmessage.value = 'account.passwordupdated';
        lostpassswordModal.value?.hide();
    }
};

/**
 * Google authentication
 */
const Google = useGoogleAuthentication(LoginOauth2);

const isDev = import.meta && import.meta.env && import.meta.env.MODE === 'development';

onMounted(() => {
    useHelper().setFocus();
});
</script>

<template>
    <SplashComp>
        <ContentComp>
            <div class="position-absolute end-0 me-5">
                <LanguageSelectorComp />
            </div>
            <form @submit.prevent.stop="loginUserRequest">
                <img
                    class="mb-4"
                    v-if="!isDev"
                    src="@/assets/img/EQWhite.png"
                    alt=""
                />
                <img
                    v-else
                    class="mb-4"
                    src="@/assets/img/EQWhite-dev.png"
                    alt=""
                />
                <h3 class="display-3 mt-5 mb-4">{{ $tm('login.pleasesignin') }}</h3>

                <div class="form-floating my-2">
                    <input
                        type="text"
                        class="form-control"
                        id="floatingInput"
                        name="username"
                        v-model="userReq.Username"
                        autofocus="true"
                        autocomplete="username"
                    />
                    <label for="floatingInput">{{ $tm('login.username') }}</label>
                </div>
                <div class="form-floating my-2">
                    <input
                        type="password"
                        class="form-control"
                        id="floatingPassword"
                        name="password"
                        v-model="userReq.Password"
                        autocomplete="current-password"
                    />
                    <label for="floatingPassword">{{ $tm('login.password') }}</label>
                </div>
                <div class="d-flex">
                    <span class="flex-fill">
                        <a
                            href="#"
                            @click="lostpassswordModal?.show()"
                            >{{ $tm('account.lostpassword') }}</a
                        > </span
                    ><a
                        v-if="false"
                        href="#"
                        @click="newaccountModal?.show()"
                        >{{ $tm('account.create') }}</a
                    >
                </div>

                <div class="form-floating my-5">
                    <ButtonComp
                        button-type="Primary"
                        class="w-100 btn-lg"
                        type="submit"
                        :loadstate="LoadState"
                    >
                        {{ $tm('button.signin') }}
                    </ButtonComp>
                </div>
                <div class="form-separator my-4">
                    <hr />
                    <div>
                        <span>{{ $tm('general.alternativelogin') }}</span>
                    </div>
                </div>
                <div
                    class="form-floating my-5"
                    v-if="Google.isLoaded"
                >
                    <ButtonComp
                        button-type="Secondary"
                        class="w-100 btn-lg"
                        textposition="left"
                        :disabled="LoadState.isActive"
                        @click="Google.showLoginDialog"
                    >
                        <IconComp
                            icon="fa-google"
                            class="me-3"
                            family="fab"
                        />

                        <span class="border border-end-0 border-bottom-0 border-top-0 ps-5"> {{ $tm('button.signingoogle') }}</span>
                    </ButtonComp>
                </div>
            </form>

            <div
                v-if="errormessage == '' && successmessage != ''"
                class="alert alert-success"
                role="alert"
            >
                <IconComp
                    :icon="featureIcons.SUCCESS"
                    class="me-2"
                />
                <span>{{ $tm(successmessage) }}</span>
            </div>

            <div
                v-if="errormessage != '' && !LoadState.isActive"
                class="alert alert-danger"
                role="alert"
            >
                <IconComp
                    icon="fa-circle-exclamation"
                    class="me-2"
                />
                <span
                    class="text-truncate"
                    :title="$tm(errormessage)"
                    >{{ $tm(errormessage) }}</span
                >
            </div>
        </ContentComp>
        <ModalComp
            :title="$tm('account.lostpasswordtitle')"
            size="md"
            :min-height="0"
            ref="lostpassswordModal"
            :close-on-escape="true"
        >
            <template #content>
                <ResetPasswordView
                    ref="resetPasswordView"
                    v-model="resetPasswordRequest"
                    @password-updated="passwordUpdated"
                ></ResetPasswordView>
            </template>
            <template #footer>
                <ButtonComp
                    v-if="!resetPasswordView?.WaitingForResetCode"
                    :loadstate="LoadState"
                    :disabled="!resetPasswordRequest.Username || resetPasswordRequest.Username.length < 3"
                    @click="AddLoader(resetPasswordView!.RequestNewPassword, '')"
                    >{{ $tm('common.send') }}</ButtonComp
                >
                <ButtonComp
                    v-else
                    @click="AddLoader(resetPasswordView?.SetNewPassword, '')"
                    :disabled="
                        resetPasswordRequest.Password!.length <= 3 ||
                        resetPasswordRequest.Code!.length < 6 ||
                        resetPasswordRequest.Password != resetPasswordRequest.ConfirmPassword
                    "
                    >{{ $tm('button.ok') }}</ButtonComp
                >
            </template>
        </ModalComp>

        <ModalComp
            :title="$tm('account.twofactorrequired')"
            size="md"
            :min-height="0"
            ref="twofactorModal"
            :close-on-escape="true"
        >
            <template #content>
                <ContentComp>
                    <div class="col-12">
                        <p v-html="$tm('account.twofactorrequireddescription', '', true)"></p>
                    </div>
                    <div class="text-center mb-5">
                        <InputCode
                            v-model="userReq.TwoFactorCode!"
                            class="form-control-lg"
                            :number-of-inputs="6"
                            @submit="loginUserRequest()"
                        ></InputCode>
                    </div>
                    <div>
                        <InputCheckBox
                            id="trustdevice"
                            v-model="userReq.TrustDevice"
                            :translationkey="{ key: 'account.trustthisdevice', default: 'Trust this device' }"
                        />
                    </div>
                    <AlertMessage
                        v-if="invalidTwofactorCode"
                        type="danger"
                        :show-icon="false"
                        ><span v-html="$tm('account.wrongtwofactorcode', '', true)"></span
                    ></AlertMessage>
                </ContentComp>
            </template>
            <template #footer>
                <ButtonComp
                    :disabled="userReq.TwoFactorCode!.length < 6"
                    @click="loginUserRequest()"
                    :loadstate="LoadState"
                    >{{ $tm('button.ok') }}</ButtonComp
                >
            </template>
        </ModalComp>
    </SplashComp>
</template>
